import { getGenericWorkspacePage } from "../WorkspaceRoutes"
import { getChecklistSubmitUrl } from "./PilotTrackingRoutes"
import { ButtonPrimary, ButtonTertiary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectChecklistDetailsTab } from "features/pilot-tracking/checklists/checklist-details/selectors"
import {
    ChecklistDetailsTab,
    setTab,
} from "features/pilot-tracking/checklists/checklist-details/slice"
import {
    useChecklist,
    useDeleteChecklist,
    useUpdateChecklist,
} from "features/pilot-tracking/checklists/ChecklistQueries"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useRouter } from "pages"
import { createPortal } from "react-dom"
import { useCallback, useRef, useLayoutEffect, useMemo } from "react"
import { useParams } from "react-router"
import {
    getGenericConfirmationModalComponent,
    getGenericDangerConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { ChecklistBuilder } from "features/pilot-tracking/checklists/ChecklistBuilder"
import { ScrollableArea } from "@nova/component-scrollable-area"
import {
    resetChecklistChangeFlags,
    selectChecklistCreationDescription,
    selectChecklistCreationQuestions,
    selectChecklistCreationTitle,
    selectChecklistDescriptionHasChanges,
    selectChecklistFormHasChanges,
    selectChecklistTitleHasChanges,
    setChecklistCreationDescription,
    setChecklistCreationQuestions,
    setChecklistCreationTitle,
} from "features/pilot-tracking/checklists/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    selectActiveWorkspace,
    selectDefaultChecklistId,
    updateWorkspace,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useChecklistUnsavedChangesBlocker } from "features/pilot-tracking/checklists/usePilotTrackingUnsavedChangesBlocker"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ChecklistResponsesSection } from "features/pilot-tracking/checklists/ChecklistResponsesSection"
import { AlertContainer } from "@nova/component-alert-container"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAircraft } from "features/pilot-tracking/aircraft/AircraftQueries"
import { usePilots } from "features/pilot-tracking/pilots/PilotQueries"
import { ChecklistForm } from "features/pilot-tracking/checklists/ChecklistForm"
import { ChecklistSubmissionWorkspaceVals } from "@nova/constants"

export const ChecklistDetailsPage = getGenericWorkspacePage({
    Header: ChecklistDetailsPageHeader,
    Content: ChecklistDetailsPageContent,
    maxWidth: 1200,
})

function ChecklistDetailsPageHeader() {
    const dispatch = useAppDispatch()
    const activeChecklistDetailsContextMenu = useAppSelector(
        selectActiveContextMenu("PilotTrackingChecklistDetails")
    )
    const router = useRouter("/workspace/:workspaceId")
    const { checklistId = "" } = useParams()
    const checklistName = useAppSelector(selectChecklistCreationTitle)
    const description = useAppSelector(selectChecklistCreationDescription)
    const questions = useAppSelector(selectChecklistCreationQuestions)
    const formHasChanges = useAppSelector(selectChecklistFormHasChanges)
    const titleHasChanges = useAppSelector(selectChecklistTitleHasChanges)
    const descriptionHasChanges = useAppSelector(selectChecklistDescriptionHasChanges)
    const contextMenuTriggerRef = useRef<HTMLDivElement>(null)
    const confirmationModal = useConfirmationModalContext()
    const { deleteChecklist } = useDeleteChecklist()
    const { updateChecklist, isMutating } = useUpdateChecklist()
    const workspace = useAppSelector(selectActiveWorkspace())
    const defaultChecklistId = useAppSelector(selectDefaultChecklistId)
    const isDefault = defaultChecklistId === checklistId
    const { data: checklist, isLoading } = useChecklist({ checklistId })

    useLayoutEffect(() => {
        if (!checklist) return
        dispatch(setChecklistCreationTitle(checklist.name))
        dispatch(setChecklistCreationDescription(checklist.description ?? null))
        dispatch(setChecklistCreationQuestions(checklist.form))
        dispatch(resetChecklistChangeFlags())
        dispatch(setTab("Questions"))
    }, [checklist, dispatch])

    const handleSetDefault = useCallback(async () => {
        if (!workspace) return
        const didConfirm = defaultChecklistId
            ? await confirmationModal.confirm({
                  RenderComponent: getGenericConfirmationModalComponent({
                      title: "Replace default checklist?",
                      message: `${checklistName} will be the new default checklist for your workspace. Are you sure?`,
                      ConfirmButtonComponent: ButtonPrimary,
                      confirmText: "Confirm",
                      leftConfirmIcon: null,
                      rightConfirmIcon: Icons.Checkmark,
                  }),
              })
            : true
        if (!didConfirm) return
        const filtered = (workspace.settings ?? []).filter((s) => s.name !== "default_checklist_id")
        dispatch(
            updateWorkspace({
                id: workspace.id,
                updates: {
                    settings: [...filtered, { name: "default_checklist_id", value: checklistId }],
                },
            })
        )
        dispatch(
            setGlobalNotificationContext({
                message: `${checklistName} is now your workspace's default checklist.`,
                timeoutInMs: 3000,
                status: GlobalNotificationStatus.success,
            })
        )
        dispatch(setActiveContextMenu(undefined))
    }, [workspace, defaultChecklistId, confirmationModal, checklistName, dispatch, checklistId])

    const handleRemoveDefault = useCallback(() => {
        if (!workspace) return
        const filtered = (workspace.settings ?? []).filter((s) => s.name !== "default_checklist_id")
        dispatch(
            updateWorkspace({
                id: workspace.id,
                updates: { settings: filtered },
            })
        )
        dispatch(
            setGlobalNotificationContext({
                message: "Successfully removed default checklist.",
                timeoutInMs: 3000,
                status: GlobalNotificationStatus.success,
            })
        )
        dispatch(setActiveContextMenu(undefined))
    }, [workspace, dispatch])

    const hasChanges = formHasChanges || titleHasChanges || descriptionHasChanges

    const handlePublish = useCallback(async () => {
        if (!hasChanges || !checklistId) return
        await updateChecklist({
            checklistId,
            ...(titleHasChanges && { name: checklistName ?? "Untitled Checklist" }),
            ...(descriptionHasChanges && { description }),
            ...(formHasChanges && { form: questions ?? [] }),
        })
            .then(() => {
                dispatch(resetChecklistChangeFlags())
                dispatch(
                    setGlobalNotificationContext({
                        message: "Checklist saved successfully.",
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 3000,
                    })
                )
            })
            .catch(() => {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to save checklist. Please try again.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 3000,
                    })
                )
            })
    }, [
        hasChanges,
        checklistId,
        updateChecklist,
        titleHasChanges,
        checklistName,
        descriptionHasChanges,
        description,
        formHasChanges,
        questions,
        dispatch,
    ])

    const { isEditor } = useWorkspacePermissions()
    useChecklistUnsavedChangesBlocker(!isEditor)

    if (isLoading) return null

    return (
        <>
            <div className="w-full h-full flex justify-between items-center">
                <div className="flex gap-4 items-center">
                    <IconButton
                        onClick={() => {
                            router.navigate({ path: "/fleet/checklists" })
                        }}
                    >
                        <Icons.Icon Icon={Icons.ArrowLeft} />
                    </IconButton>
                    <div className="text-[1.375rem] telegraf">{checklistName}</div>
                    {hasChanges && <small className="text-gray-300">* Unsaved changes</small>}
                </div>
                {isEditor && (
                    <div className="flex items-center gap-2">
                        <div ref={contextMenuTriggerRef}>
                            <ButtonTertiary
                                RightIcon={Icons.ThreeDotsSettings}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (!!activeChecklistDetailsContextMenu) {
                                        dispatch(setActiveContextMenu(undefined))
                                        return
                                    }
                                    const rect =
                                        contextMenuTriggerRef.current?.getBoundingClientRect()
                                    if (!rect) return
                                    dispatch(
                                        setActiveContextMenu({
                                            type: "PilotTrackingChecklistDetails",
                                            position: {
                                                top: rect.top + rect.height + 4,
                                                left: rect.left,
                                            },
                                        })
                                    )
                                }}
                                className={classNames("!px-2 !min-w-fit", {
                                    "!border-gray-100 !text-gray-200":
                                        !!activeChecklistDetailsContextMenu,
                                })}
                            />
                        </div>
                        <ButtonPrimary
                            title="Publish"
                            RightIcon={Icons.Upload}
                            onClick={handlePublish}
                            disabled={!hasChanges || isMutating}
                        />
                    </div>
                )}
            </div>
            {activeChecklistDetailsContextMenu &&
                createPortal(
                    <ContextMenu
                        sections={[
                            [
                                {
                                    title: isDefault ? "Remove as Default" : "Set as Default",
                                    Icon: Icons.PinDrop,
                                    onClick: isDefault ? handleRemoveDefault : handleSetDefault,
                                },
                                {
                                    title: "Fill Out",
                                    Icon: Icons.OpenInNew,
                                    onClick: () => {
                                        if (!workspace) return
                                        window.open(
                                            getChecklistSubmitUrl({
                                                orgId: workspace.id,
                                                checklistId,
                                            }),
                                            "_blank",
                                            "noreferrer"
                                        )
                                    },
                                },
                            ],
                            [
                                {
                                    title: "Delete",
                                    Icon: Icons.Trash,
                                    onClick: () => {
                                        void confirmationModal.confirm({
                                            RenderComponent:
                                                getGenericDangerConfirmationModalComponent({
                                                    title: "Delete Checklist?",
                                                    message: `Are you sure you want to delete ${checklistName}?`,
                                                    dangerMessage: "This cannot be undone.",
                                                }),
                                            asyncConfirmCallback: async () => {
                                                if (!checklistId) return
                                                await deleteChecklist(checklistId)
                                                if (isDefault && workspace) {
                                                    const filtered = (
                                                        workspace.settings ?? []
                                                    ).filter(
                                                        (s) => s.name !== "default_checklist_id"
                                                    )
                                                    await dispatch(
                                                        updateWorkspace({
                                                            id: workspace.id,
                                                            updates: { settings: filtered },
                                                        })
                                                    )
                                                }
                                                router.navigate({ path: "/fleet/checklists" })
                                            },
                                        })
                                    },
                                    isDestructive: true,
                                },
                            ],
                        ]}
                        onBlur={() => dispatch(setActiveContextMenu(undefined))}
                        isFixedPosition={true}
                        top={activeChecklistDetailsContextMenu.position.top}
                        left={activeChecklistDetailsContextMenu.position.left}
                    />,
                    document.getElementById("portal")!
                )}
        </>
    )
}

function ChecklistDetailsPageContent() {
    const tab = useAppSelector(selectChecklistDetailsTab)
    const { checklistId = "" } = useParams()
    const { isLoading } = useChecklist({ checklistId })
    if (isLoading) return null
    return (
        <div className="flex flex-col h-full">
            <ChecklistDetailsTabSwitcher />
            {tab === "Questions" && (
                <ScrollableArea className="flex-1 py-8 w-full">
                    <div className="flex justify-center">
                        <ChecklistBuilder />
                    </div>
                </ScrollableArea>
            )}
            {tab === "Preview" && <ChecklistPreviewTab />}
            {tab === "Responses" && <ChecklistResponsesSection />}
        </div>
    )
}

function ChecklistPreviewTab() {
    const title = useAppSelector(selectChecklistCreationTitle)
    const description = useAppSelector(selectChecklistCreationDescription)
    const questions = useAppSelector(selectChecklistCreationQuestions)
    const workspace = useAppSelector(selectActiveWorkspace())
    const members = useAppSelector(selectWorkspaceMembers)
    const { aircraft } = useAircraft()
    const { activeUserIds } = usePilots()

    const workspaceVals = useMemo<ChecklistSubmissionWorkspaceVals>(
        () => ({
            userId: "",
            workspaceName: workspace?.name ?? "",
            workspaceMembers: members.map((m) => ({
                id: m.userId,
                fullName: m.fullName ?? m.userId,
                isActive: activeUserIds.has(m.userId),
            })),
            workspaceAircrafts: aircraft,
        }),
        [workspace, members, aircraft, activeUserIds]
    )

    return (
        <ScrollableArea className="flex-1 py-8 w-full">
            <div className="mx-auto w-full max-w-xl flex flex-col gap-8">
                <AlertContainer type="info" title="This is a preview of what pilots will see." />
                <div className="flex justify-between items-center gap-1.5">
                    <Icons.NovaLogoTitle />
                    <small className="xs text-gray-300 break-words text-right">
                        {workspaceVals.workspaceName}
                    </small>
                </div>
                <ChecklistForm
                    mode="preview"
                    checklist={{ name: title ?? "", description, form: questions ?? [] }}
                    workspaceVals={workspaceVals}
                    showLinkingForm
                />
            </div>
        </ScrollableArea>
    )
}

function ChecklistDetailsTabSwitcher() {
    return (
        <div className="w-full flex items-end">
            <TabItem tab="Questions" />
            <div className="w-6 border-b border-b-white-200" />
            <TabItem tab="Preview" />
            <div className="w-6 border-b border-b-white-200" />
            <TabItem tab="Responses" />
            <div className="grow border-b border-b-white-200" />
        </div>
    )
}

function TabItem({ tab }: { tab: ChecklistDetailsTab }) {
    const dispatch = useAppDispatch()
    const activeTab = useAppSelector(selectChecklistDetailsTab)
    return (
        <div
            className={classNames("border-b px-1 py-3 transition-colors", {
                "border-b-blue-500 text-white": activeTab === tab,
                "border-b-white-200 text-gray-200 cursor-pointer": activeTab !== tab,
            })}
            onClick={() => {
                if (activeTab === tab) return
                dispatch(setTab(tab))
            }}
        >
            {tab}
        </div>
    )
}
